import React, { useState, useEffect } from 'react';
import { getAllItems, restockConsumable } from '../../utils/itemOperations';

const SupplyManagementDashboard = ({ onAddConsumable }) => {
  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restockModal, setRestockModal] = useState({ isOpen: false, item: null });
  const [restockAmount, setRestockAmount] = useState('');
  const [restockNotes, setRestockNotes] = useState('');
  const [restockLoading, setRestockLoading] = useState(false);

  useEffect(() => {
    loadConsumables();
  }, []);

  const loadConsumables = async () => {
    try {
      setLoading(true);
      setError('');
      
      const allItems = await getAllItems();
      const consumableItems = allItems
        .filter(item => item.itemType === 'consumable')
        .map(item => {
          const currentStock = parseFloat(item.currentStock) || 0;
          const initialStock = parseFloat(item.initialStock) || 0;
          const lowStockAlert = parseFloat(item.lowStockAlert) || 0;
          const stockPercentage = initialStock > 0 ? (currentStock / initialStock) * 100 : 0;
          
          return {
            ...item,
            currentStock,
            initialStock,
            lowStockAlert,
            stockPercentage,
            needsRestock: lowStockAlert > 0 ? currentStock <= lowStockAlert : stockPercentage < 25,
            priority: currentStock === 0 ? 'urgent' : 
                     (lowStockAlert > 0 && currentStock <= lowStockAlert) ? 'high' :
                     stockPercentage < 25 ? 'medium' : 'low'
          };
        })
        .sort((a, b) => {
          // Sort by priority: urgent > high > medium > low
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          }
          // Then by stock percentage (lowest first)
          return a.stockPercentage - b.stockPercentage;
        });

      setConsumables(consumableItems);
    } catch (error) {
      console.error('Error loading consumables:', error);
      setError('Failed to load consumables');
    } finally {
      setLoading(false);
    }
  };

  const handleRestockClick = (item) => {
    setRestockModal({ isOpen: true, item });
    setRestockAmount('');
    setRestockNotes('');
  };

  const handleRestockSubmit = async (e) => {
    e.preventDefault();
    
    if (!restockAmount || parseFloat(restockAmount) <= 0) {
      alert('Please enter a valid restock amount');
      return;
    }

    try {
      setRestockLoading(true);
      await restockConsumable(
        restockModal.item.id, 
        parseFloat(restockAmount), 
        restockNotes || 'Monthly supply restock'
      );
      
      setRestockModal({ isOpen: false, item: null });
      loadConsumables(); // Refresh the list
    } catch (error) {
      console.error('Error restocking item:', error);
      alert('Failed to restock item: ' + error.message);
    } finally {
      setRestockLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    
    const labels = {
      urgent: 'Out of Stock',
      high: 'Low Stock',
      medium: 'Running Low',
      low: 'Good Stock'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${badges[priority]}`}>
        {labels[priority]}
      </span>
    );
  };

  // Feature disabled until team approves
  // const getShoppingList = () => {
  //   return consumables
  //     .filter(item => item.needsRestock || item.priority === 'urgent')
  //     .map(item => `${item.name} (${item.unit})`)
  //     .join('\n');
  // };

  // const exportShoppingList = () => {
  //   const shoppingList = getShoppingList();
  //   const blob = new Blob([shoppingList], { type: 'text/plain' });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url;
  //   a.download = `lab-shopping-list-${new Date().toISOString().split('T')[0]}.txt`;
  //   document.body.appendChild(a);
  //   a.click();
  //   document.body.removeChild(a);
  //   URL.revokeObjectURL(url);
  // };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-lg text-gray-600">Loading supply management data...</div>
        </div>
      </div>
    );
  }

  const urgentItems = consumables.filter(item => item.priority === 'urgent');
  const lowStockItems = consumables.filter(item => item.priority === 'high');
  const runningLowItems = consumables.filter(item => item.priority === 'medium');

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Supply Management</h2>
            <p className="text-gray-600">Track consumables for monthly hardware store runs</p>
          </div>
          <div className="mt-4 lg:mt-0 flex gap-3">
            <button
              onClick={onAddConsumable}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Add Consumable
            </button>
            <button
              onClick={loadConsumables}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-700">{urgentItems.length}</div>
          <div className="text-sm text-red-600">Out of Stock</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-700">{lowStockItems.length}</div>
          <div className="text-sm text-orange-600">Low Stock</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-700">{runningLowItems.length}</div>
          <div className="text-sm text-yellow-600">Running Low</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-700">{consumables.length}</div>
          <div className="text-sm text-blue-600">Total Consumables</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4">
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Priority Items List */}
      {urgentItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-red-700 mb-4">🚨 Urgent - Out of Stock</h3>
          <div className="space-y-3">
            {urgentItems.map(item => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{item.name}</div>
                  <div className="text-sm text-gray-600">{item.category || 'Uncategorized'}</div>
                  <div className="text-sm text-red-600 font-medium">0 {item.unit} remaining</div>
                </div>
                <button
                  onClick={() => handleRestockClick(item)}
                  className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  Restock Now
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Consumables Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Consumables</h3>
        </div>
        
        {consumables.length === 0 ? (
          <div className="p-6 text-center">
            <div className="text-gray-500 mb-4">
              No consumables found. Get started by adding your first consumable item.
            </div>
            <button
              onClick={onAddConsumable}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            >
              Add Your First Consumable
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {consumables.map(item => (
                  <tr key={item.id} className={item.priority === 'urgent' ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-sm text-gray-500">{item.category || 'Uncategorized'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {item.currentStock} / {item.initialStock} {item.unit}
                      </div>
                      {item.costPerUnit && (
                        <div className="text-xs text-gray-500">
                          ${item.costPerUnit}/unit
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                        <div
                          className={`h-2 rounded-full ${
                            item.stockPercentage > 50 ? 'bg-green-500' :
                            item.stockPercentage > 25 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.max(item.stockPercentage, 2)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500">{item.stockPercentage.toFixed(1)}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(item.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleRestockClick(item)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Restock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restock Modal */}
      {restockModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Restock {restockModal.item?.name}
              </h3>
              
              <form onSubmit={handleRestockSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount to Add *
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      value={restockAmount}
                      onChange={(e) => setRestockAmount(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      required
                    />
                    <span className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-gray-700">
                      {restockModal.item?.unit}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={restockNotes}
                    onChange={(e) => setRestockNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="e.g., Monthly hardware store run, Emergency restock"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setRestockModal({ isOpen: false, item: null })}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={restockLoading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {restockLoading ? 'Restocking...' : 'Restock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyManagementDashboard;
